import type { EditorState } from '@tiptap/pm/state'
import type { Node as ProsNode } from '@tiptap/pm/model'
import { getCharAround } from '@/services/forms/tmTiptap/utils/getCharAround'

import { linkMarkName, tagNodeName } from '@/services/forms/tmTiptap/const'

type ShouldBeSpaceInput = {
  contentType: string
  state: EditorState
  nodeBefore: ProsNode | null
  nodeAfter: ProsNode | null
  from: number
  to: number
}
type NeedsSpaceAroundInput = {
  node: ProsNode | null
  state: EditorState
  from: number
  to: number
  isBefore?: boolean
}
const needsSpaceAround = ({ node, state, from, to, isBefore }: NeedsSpaceAroundInput) => {
  if (!node) return false // The beginning of the line
  // Checking if a node is text and if it is preceded by a space
  return !(node.type.name === 'text' && /\s/.test(getCharAround(state, from, to, isBefore)))
}
export const shouldBeSpace = ({ contentType, state, nodeBefore, nodeAfter, from, to }: ShouldBeSpaceInput) => {
  const defaultHandler = () => false
  const contentTypeHandlers = {
    text: (node: ProsNode | null, isBefore: boolean) => needsSpaceAround({ node, state, from, to, isBefore }),
    [linkMarkName]: (node: ProsNode | null, isBefore: boolean) => needsSpaceAround({ node, state, from, to, isBefore }),
    [tagNodeName]: (node: ProsNode | null, isBefore: boolean) => {
      if (!isBefore && !node) {
        return true // Always add a space after the tag if it is missing
      }
      return needsSpaceAround({ node, state, from, to, isBefore })
    },
  }

  return [nodeBefore, nodeAfter].map((node, i) => {
    const handler = contentTypeHandlers[contentType] || defaultHandler
    return handler(node, !i)
  })
}
export const ensureSpacingAround = (contentType: string, content: string, state: EditorState) => {
  const { from, to } = state.selection
  const { nodeBefore, nodeAfter } = state.doc.resolve(from)
  const props = { state, contentType, nodeBefore, nodeAfter, from, to }
  const [needSpaceBefore, needSpaceAfter] = shouldBeSpace(props)
  const spaceBefore = needSpaceBefore ? ' ' : ''
  const spaceAfter = needSpaceAfter ? ' ' : ''

  return `${spaceBefore}${content}${spaceAfter}`
}
